import { useCallback, useState } from 'react';
import {
	Box,
	IconButton,
	Stack,
	useMediaQuery,
	AppBar,
	Toolbar,
	Chip,
	CircularProgress,
	Fade,
	Snackbar,
	Alert,
	Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AccountButton } from '../account-button';
import { useTheme } from '@mui/material/styles';
import { useShopDetails } from '../../hooks/use-shop';
import { TOP_NAV_HEIGHT, useNavTransition, useSideNavWidth } from './layout-constants';

const NOTIFICATION_DURATION = 4000;
const FADE_DURATION = {
	enter: 200,
	exit: 100
};

interface TopNavProps {
	/** Desktop: collapse/expand the rail. Mobile: open the drawer. */
	onMenuToggle: () => void;
	collapsed: boolean;
}

export const TopNav = ({ onMenuToggle, collapsed }: TopNavProps) => {
	const theme = useTheme();
	const { locationName } = useShopDetails();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const mdUp = useMediaQuery(theme.breakpoints.up('md'));
	const navWidth = useSideNavWidth(collapsed);
	const transition = useNavTransition(['margin-left', 'width']);

	const menuLabel = !mdUp ? 'Open menu' : collapsed ? 'Expand menu' : 'Collapse menu';

	const handleLoadingChange = useCallback((loading: boolean, error?: string) => {
		setIsLoading(loading);
		if (error) {
			setError(error);
		}
	}, []);

	const handleCloseError = useCallback(() => {
		setError(null);
	}, []);

	return (
		<>
			<AppBar
				sx={{
					backdropFilter: 'blur(6px)',
					backgroundColor: alpha(theme.palette.background.default, 0.8),
					position: 'sticky',
					top: 0,
					ml: `${navWidth}px`,
					width: `calc(100% - ${navWidth}px)`,
					transition,
					zIndex: theme.zIndex.appBar
				}}
			>
				<Toolbar
					disableGutters
					sx={{
						minHeight: TOP_NAV_HEIGHT,
						px: 2,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between'
					}}
				>
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Tooltip title={menuLabel}>
							<IconButton
								onClick={onMenuToggle}
								aria-label={menuLabel}
								aria-expanded={mdUp ? !collapsed : undefined}
								aria-controls="app-side-nav"
								edge="start"
							>
								{mdUp && !collapsed ? <MenuOpenIcon /> : <MenuIcon />}
							</IconButton>
						</Tooltip>
						{locationName && (
							<Box sx={{ position: 'relative', minHeight: 40, display: 'flex', alignItems: 'center' }}>
								<Fade 
									in={isLoading} 
									timeout={{
										enter: 200,
										exit: 100,
									}}
								>
									<CircularProgress 
										size={20} 
										sx={{ 
											position: 'absolute',
											left: -28
										}}
									/>
								</Fade>
								<Chip
									icon={<LocationOnIcon />}
									label={locationName}
									size="small"
									sx={{
										height: 24,
										borderRadius: 1,
										pl: 0.5,
										backgroundColor: theme.palette.mode === 'dark' 
											? alpha(theme.palette.primary.main, 0.2)
											: alpha(theme.palette.primary.main, 0.1),
										'.MuiChip-icon': {
											color: theme.palette.primary.main
										},
										'.MuiChip-label': {
											color: theme.palette.primary.main,
											px: 1.5,
											py: 0.5
										}
									}}
								/>
							</Box>
						)}
					</Stack>
					<Stack direction="row" spacing={2} alignItems="center">
						<AccountButton onLoadingChange={handleLoadingChange} />
					</Stack>
				</Toolbar>
			</AppBar>

			<Snackbar
				open={!!error}
				onClose={handleCloseError}
				autoHideDuration={NOTIFICATION_DURATION}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				TransitionComponent={Fade}
				TransitionProps={{
					timeout: FADE_DURATION
				}}
			>
				<Alert
					icon={<ErrorOutlineIcon fontSize="inherit" />}
					severity="error"
					variant="filled"
					onClose={handleCloseError}
					sx={{ width: '100%' }}
				>
					{error}
				</Alert>
			</Snackbar>
		</>
	);
};
